/**
 * W70 Phase 3 (2026-05-25) — Demo-Variante als Live-Preview.
 *
 * Statt 4 separate Demo-Pages zu pflegen, nutzt die /demo-Route ab jetzt
 * die echten Dashboard/Watchlist/SymbolDetail/Strategies-Components,
 * gewrapped in DemoModeProvider. Damit ist die Demo immer aktuell + kein
 * Doppel-Aufwand.
 *
 * KORREKTUR 20.09.2026: Hier stand bis heute „Authentifizierung: KEINE. Die
 * Watchlist-RLS (`watchlist_legacy_anon_select`) erlaubt anon-Read auf die
 * Owner-Watchlist — Demo-User sieht die echten Marktdaten + Bewertungen."
 * Das stimmt seit der RLS-Reparatur vom 10.08.2026 nicht mehr: Die Policy
 * heisst noch so, verlangt seitdem aber die Rolle `authenticated` (geprüft
 * gegen die Datenbank). Ein Besucher ohne Session sah hier seitdem
 * kommentarlos eine leere Watchlist — derselbe Fehler, den der Login-Balken
 * im Rest der App am selben Tag behoben hat. `NichtAngemeldetBanner` läuft
 * deshalb jetzt auch hier.
 *
 * Sidebar nur 3 Items: Dashboard, Watchlist, Strategien.
 */

import { lazy, Suspense, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { LineChart, ListChecks, Target, Menu, X, Eye } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { cn } from '@/lib/utils';
import { DemoModeProvider } from '@/contexts/DemoModeContext';
import { DemoBanner } from '@/components/demo/DemoBanner';
import { NichtAngemeldetBanner } from '@/components/NichtAngemeldetBanner';
import { useAuth } from '@/hooks/use-auth';
import Dashboard from '@/pages/Dashboard';
import Watchlist from '@/pages/Watchlist';

// Lazy-Loaded fuer kleinere Initial-Bundle-Size
const SymbolDetail = lazy(() => import('@/pages/SymbolDetail'));
const Strategies = lazy(() => import('@/pages/Strategies'));

interface DemoNavItem {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
}

const NAV: readonly DemoNavItem[] = [
  { to: '/demo', label: 'Dashboard', icon: LineChart, end: true },
  { to: '/demo/watchlist', label: 'Watchlist', icon: ListChecks },
  { to: '/demo/strategien', label: 'Strategien', icon: Target },
];

const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center text-zinc-500">
    Lade Demo…
  </div>
);

export default function DemoApp() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { status } = useAuth();

  return (
    <DemoModeProvider>
      <div className="flex min-h-screen bg-cockpit-base text-zinc-100">
        {/* Mobile-Top-Bar */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between border-b border-cockpit-border bg-cockpit-base/95 backdrop-blur px-3 h-12">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-1.5 text-zinc-300 hover:bg-cockpit-surface2 focus-ring"
            aria-label="Navigation öffnen"
          >
            <Menu className="size-5" />
          </button>
          <div className="flex items-center gap-1.5">
            <div className="size-6 rounded bg-cockpit-accent/15 border border-cockpit-accent/40 flex items-center justify-center text-cockpit-accent text-xs font-bold">
              S
            </div>
            <span className="text-xs font-semibold tracking-wider">SwingTrader</span>
            <span className="rounded bg-zinc-800 border border-zinc-700 text-[9px] uppercase tracking-wider text-zinc-300 px-1.5 py-0.5">
              Demo
            </span>
          </div>
          <a
            href="/login"
            className="text-[10px] text-cockpit-accent hover:underline"
          >
            Login
          </a>
        </header>

        {/* Sidebar */}
        <aside
          className={cn(
            'fixed lg:sticky lg:top-0 left-0 top-0 z-40 h-screen w-64 lg:w-56 shrink-0 border-r border-cockpit-border bg-cockpit-surface backdrop-blur transition-transform',
            mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          )}
        >
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <div className="size-7 rounded bg-cockpit-accent/15 border border-cockpit-accent/40 flex items-center justify-center text-cockpit-accent font-bold">
                S
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">SwingTrader</div>
                <div className="text-[10px] uppercase tracking-wider text-cockpit-accent flex items-center gap-1">
                  <Eye className="size-2.5" />
                  Demo / Live-Preview
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="lg:hidden rounded-md p-1 text-zinc-400 hover:text-zinc-100 hover:bg-cockpit-surface2 focus-ring"
              aria-label="Navigation schließen"
            >
              <X className="size-4" />
            </button>
          </div>

          <nav className="p-2 flex flex-col gap-0.5">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end ?? false}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-all focus-ring',
                    isActive
                      ? 'bg-cockpit-accent-soft text-zinc-50 font-medium'
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-cockpit-surface2/60'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span
                        className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-cockpit-accent"
                        aria-hidden="true"
                      />
                    )}
                    <Icon
                      className={cn(
                        'size-4 transition-colors',
                        isActive ? 'text-cockpit-accent' : 'text-zinc-500 group-hover:text-zinc-300'
                      )}
                    />
                    <span>{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 px-3 py-3 border-t border-cockpit-border space-y-2">
            <a
              href="/login"
              className="block rounded border border-cockpit-accent/40 bg-cockpit-accent/10 px-2 py-1.5 text-center text-[11px] font-medium text-cockpit-accent hover:bg-cockpit-accent/20 transition-colors"
            >
              Registrieren / Login
            </a>
            <div className="text-[10px] text-zinc-500 leading-tight">
              Live-Preview der echten App. Trades, Pläne und Einstellungen
              sind hier ausgeblendet.
            </div>
            <div className="text-[10px] text-zinc-500 font-mono">
              <span className="text-cockpit-success">●</span> live
            </div>
          </div>
        </aside>

        {/* Backdrop */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-30 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        <main className="flex-1 min-w-0 pt-12 lg:pt-0">
          {/* Sticky-Banner ueber dem Content */}
          <DemoBanner />
          {status === 'anonymous' && <NichtAngemeldetBanner />}

          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="watchlist" element={<Watchlist />} />
              <Route path="strategien" element={<Strategies />} />
              <Route path="symbol/:layer/:symbol" element={<SymbolDetail />} />
              {/* Alles andere → zurück zum Demo-Dashboard */}
              <Route path="*" element={<Navigate to="/demo" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </DemoModeProvider>
  );
}
